import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../../utils/firebase';
import { doc, getDoc, setDoc, getDocs, collection, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Plus, Trash2, ChevronUp, ChevronDown, Save, Image, Loader2, ClipboardCheck, Check } from 'lucide-react';
import { ShopKitModel } from '../../types/shop';

// ═══════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════

export interface QuestionnaireStyleItem {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  order: number;
}

export interface QuestionnaireConfig {
  frameStyles: QuestionnaireStyleItem[];
  joineryStyles: QuestionnaireStyleItem[];
  portfolioDesigns: QuestionnaireStyleItem[];
  selectedKitIds?: string[];
  portfolioSectionTitle?: string;
  kitNameOverrides?: Record<string, string>;
  speciesImages?: Record<string, string>;
  updatedAt?: any;
}

const SPECIES_LIST = [
  { id: 'white-pine', name: 'Eastern White Pine' },
  { id: 'douglas-fir', name: 'Douglas Fir' },
  { id: 'white-oak', name: 'White Oak' },
] as const;

// Default content (matches current hardcoded values)
const DEFAULT_FRAME_STYLES: QuestionnaireStyleItem[] = [
  { id: 'king-post', title: 'King Post', description: 'A classic triangular truss with a single central post. Simple, strong, and timeless — ideal for open great rooms and traditional homes.', imageUrl: '/questionnaire/king-post.png', order: 0 },
  { id: 'queen-post', title: 'Queen Post', description: 'Two vertical posts create a wider opening beneath. Perfect for larger spans where you want an open feel with structural integrity.', imageUrl: '/questionnaire/queen-post.png', order: 1 },
  { id: 'hammer-beam', title: 'Hammer Beam', description: 'A dramatic, medieval-inspired design with short beams projecting from the walls. Creates soaring interior height and a stunning visual statement.', imageUrl: '/questionnaire/hammer-beam.png', order: 2 },
  { id: 'scissor-truss', title: 'Scissor Truss', description: 'Crossing diagonal members create a vaulted ceiling effect. Modern and elegant — great for cathedral-style spaces.', imageUrl: '/questionnaire/scissor-truss.png', order: 3 },
  { id: 'cruck-frame', title: 'Cruck Frame', description: 'Curved timbers rise from the ground to the ridge, forming a natural arch. An old-world look with incredible character.', imageUrl: '/questionnaire/cruck-frame.png', order: 4 },
  { id: 'post-and-beam', title: 'Post & Beam', description: 'The most versatile and widely used style. Vertical posts support horizontal beams with knee braces — clean, open, and adaptable to any layout.', imageUrl: '/questionnaire/post-and-beam.png', order: 5 },
  { id: 'timber-pavilion', title: 'Timber Pavilion', description: 'An open-air structure with exposed beams and no walls. Perfect for outdoor living, entertaining, or a backyard retreat.', imageUrl: '/questionnaire/timber-pavilion.png', order: 6 },
  { id: 'cathedral-vault', title: 'Cathedral Vault', description: 'Soaring arched braces create a grand, awe-inspiring interior. The ultimate statement for large living spaces, chapels, or event venues.', imageUrl: '/questionnaire/cathedral-vault.png', order: 7 },
];

const DEFAULT_JOINERY_STYLES: QuestionnaireStyleItem[] = [
  { id: 'mortise-tenon', title: 'Mortise & Tenon', description: 'The foundation of timber framing — a rectangular tenon fits snugly into a matching cavity. Used for thousands of years.', imageUrl: '/questionnaire/mortise-tenon.png', order: 0 },
  { id: 'dovetail', title: 'Dovetail', description: 'A fan-shaped joint that locks members together. Extremely strong and visually distinctive.', imageUrl: '/questionnaire/dovetail.png', order: 1 },
  { id: 'lap-joint', title: 'Lap Joint', description: 'Two members overlap with material removed from each, creating a flush connection. Clean and efficient.', imageUrl: '/questionnaire/lap-joint.png', order: 2 },
  { id: 'through-tenon-wedge', title: 'Through-Tenon with Wedge', description: 'The tenon passes completely through and is locked with wedges. A beautiful detail that showcases craftsmanship.', imageUrl: '/questionnaire/through-tenon-wedge.png', order: 3 },
  { id: 'pegged-joints', title: 'Pegged Joints', description: 'Wooden pegs lock joints together using a drawboring technique. Traditional, all-wood construction without metal fasteners.', imageUrl: '/questionnaire/pegged-joints.png', order: 4 },
  { id: 'steel-gusset-hybrid', title: 'Steel Gusset Hybrid', description: 'Steel plates and bolts connect timber members. A modern approach that combines industrial aesthetics with timber warmth.', imageUrl: '/questionnaire/steel-gusset-hybrid.png', order: 5 },
];

// ═══════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════

const QuestionnaireSettingsTab: React.FC = () => {
  const [config, setConfig] = useState<QuestionnaireConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [allKits, setAllKits] = useState<ShopKitModel[]>([]);
  const [selectedKitIds, setSelectedKitIds] = useState<string[]>([]);
  const [portfolioSectionTitle, setPortfolioSectionTitle] = useState('Explore Full Timber Frame Designs');
  const [kitNameOverrides, setKitNameOverrides] = useState<Record<string, string>>({});
  const [speciesImages, setSpeciesImages] = useState<Record<string, string>>({});
  const [uploadingSpecies, setUploadingSpecies] = useState<string | null>(null);

  // Load config from Firestore
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'appSettings', 'questionnaire'));
        if (snap.exists()) {
          const data = snap.data() as QuestionnaireConfig;
          setConfig({
            frameStyles: (data.frameStyles || []).sort((a, b) => a.order - b.order),
            joineryStyles: (data.joineryStyles || []).sort((a, b) => a.order - b.order),
            portfolioDesigns: (data.portfolioDesigns || []).sort((a, b) => a.order - b.order),
            selectedKitIds: data.selectedKitIds || [],
          });
          setSelectedKitIds(data.selectedKitIds || []);
          if (data.portfolioSectionTitle) setPortfolioSectionTitle(data.portfolioSectionTitle);
          if (data.kitNameOverrides) setKitNameOverrides(data.kitNameOverrides);
          if (data.speciesImages) setSpeciesImages(data.speciesImages);
        } else {
          const defaultConfig: QuestionnaireConfig = {
            frameStyles: DEFAULT_FRAME_STYLES,
            joineryStyles: DEFAULT_JOINERY_STYLES,
            portfolioDesigns: [],
            selectedKitIds: [],
          };
          setConfig(defaultConfig);
        }
      } catch (err) {
        console.error('Error loading questionnaire config:', err);
        setConfig({ frameStyles: DEFAULT_FRAME_STYLES, joineryStyles: DEFAULT_JOINERY_STYLES, portfolioDesigns: [], selectedKitIds: [] });
      } finally {
        setLoading(false);
      }
    };
    load();

    // Load all kits from timber_kits
    const loadKits = async () => {
      try {
        const snap = await getDocs(collection(db, 'shop_catalog'));
        const kits = snap.docs.map(d => ({ id: d.id, ...d.data() } as ShopKitModel))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        setAllKits(kits);
      } catch (err) { console.error('Error loading kits:', err); }
    };
    loadKits();
  }, []);

  const handleSave = async (overrideKitIds?: string[]) => {
    if (!config) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'appSettings', 'questionnaire'), {
        frameStyles: config.frameStyles.map((s, i) => ({ ...s, order: i })),
        joineryStyles: config.joineryStyles.map((s, i) => ({ ...s, order: i })),
        portfolioDesigns: config.portfolioDesigns.map((s, i) => ({ ...s, order: i })),
        selectedKitIds: overrideKitIds ?? selectedKitIds,
        portfolioSectionTitle,
        kitNameOverrides,
        speciesImages,
        updatedAt: Timestamp.now(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Error saving questionnaire config:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (category: 'frameStyles' | 'joineryStyles' | 'portfolioDesigns', itemId: string, file: File) => {
    setUploadingImage(itemId);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const storagePath = `media-library/questionnaire/${category}/${itemId}.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      setConfig(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          [category]: prev[category].map(s => s.id === itemId ? { ...s, imageUrl: url } : s),
        };
      });
    } catch (err) {
      console.error('Error uploading image:', err);
    } finally {
      setUploadingImage(null);
    }
  };

  const addItem = (category: 'frameStyles' | 'joineryStyles' | 'portfolioDesigns') => {
    if (!config) return;
    const newId = `custom-${Date.now()}`;
    const newItem: QuestionnaireStyleItem = {
      id: newId,
      title: category === 'portfolioDesigns' ? 'New Project' : 'New Style',
      description: 'Add a description...',
      imageUrl: '',
      order: config[category].length,
    };
    setConfig(prev => prev ? { ...prev, [category]: [...prev[category], newItem] } : prev);
    setEditingItem(newId);
  };

  const removeItem = (category: 'frameStyles' | 'joineryStyles' | 'portfolioDesigns', itemId: string) => {
    if (!config || !window.confirm('Remove this style?')) return;
    setConfig(prev => prev ? { ...prev, [category]: prev[category].filter(s => s.id !== itemId) } : prev);
  };

  const moveItem = (category: 'frameStyles' | 'joineryStyles' | 'portfolioDesigns', index: number, direction: -1 | 1) => {
    if (!config) return;
    const items = [...config[category]];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setConfig(prev => prev ? { ...prev, [category]: items } : prev);
  };

  const updateItem = (category: 'frameStyles' | 'joineryStyles' | 'portfolioDesigns', itemId: string, field: 'title' | 'description', value: string) => {
    setConfig(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [category]: prev[category].map(s => s.id === itemId ? { ...s, [field]: value } : s),
      };
    });
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}><Loader2 size={24} className="spin" /> Loading...</div>;
  }

  if (!config) return null;

  const renderStyleList = (category: 'frameStyles' | 'joineryStyles' | 'portfolioDesigns', label: string) => (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>{label} ({config[category].length})</h3>
        <button
          onClick={() => addItem(category)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
            background: '#2C3E2F', color: '#fff', border: 'none', borderRadius: 8,
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          <Plus size={14} /> Add Style
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {config[category].map((item, index) => {
          const isEditing = editingItem === item.id;
          return (
            <div
              key={item.id}
              style={{
                display: 'flex', gap: 12, padding: 12, background: 'var(--color-surface)',
                border: '1px solid var(--color-border)', borderRadius: 10, alignItems: 'flex-start',
              }}
            >
              {/* Reorder */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                <button onClick={() => moveItem(category, index, -1)} disabled={index === 0}
                  style={{ background: 'none', border: 'none', cursor: index === 0 ? 'default' : 'pointer', padding: 2, color: index === 0 ? '#D1D5DB' : '#6B7280' }}>
                  <ChevronUp size={16} />
                </button>
                <button onClick={() => moveItem(category, index, 1)} disabled={index === config[category].length - 1}
                  style={{ background: 'none', border: 'none', cursor: index === config[category].length - 1 ? 'default' : 'pointer', padding: 2, color: index === config[category].length - 1 ? '#D1D5DB' : '#6B7280' }}>
                  <ChevronDown size={16} />
                </button>
              </div>

              {/* Image */}
              <div style={{ width: 72, height: 72, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: 'var(--pm-surface-hover)', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Image size={24} style={{ color: 'var(--color-text-muted)' }} />
                )}
                {uploadingImage === item.id && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Loader2 size={18} className="spin" />
                  </div>
                )}
                <label style={{
                  position: 'absolute', inset: 0, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(0,0,0,0.5)', opacity: 0, transition: 'opacity 0.2s',
                  color: '#fff', fontSize: 10, fontWeight: 600, fontFamily: 'Inter, sans-serif',
                  flexDirection: 'column', gap: 2,
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
                >
                  <Image size={14} />
                  Change
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(category, item.id, f); }} />
                </label>
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {isEditing ? (
                  <>
                    <input
                      value={item.title}
                      onChange={e => updateItem(category, item.id, 'title', e.target.value)}
                      placeholder="Style title"
                      style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14, fontWeight: 600, marginBottom: 6, fontFamily: 'Inter, sans-serif' }}
                    />
                    <textarea
                      value={item.description}
                      onChange={e => updateItem(category, item.id, 'description', e.target.value)}
                      placeholder="Description"
                      rows={2}
                      style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'Inter, sans-serif' }}
                    />
                    <button onClick={() => setEditingItem(null)} style={{ marginTop: 4, fontSize: 12, color: 'var(--pm-accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                      Done Editing
                    </button>
                  </>
                ) : (
                  <div onClick={() => setEditingItem(item.id)} style={{ cursor: 'pointer' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 2 }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>{item.description}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>Click to edit</div>
                  </div>
                )}
              </div>

              {/* Delete */}
              <button onClick={() => removeItem(category, item.id)}
                style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error-text)', padding: 4 }}>
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ClipboardCheck size={20} style={{ color: 'var(--pm-accent)' }} />
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Design Questionnaire</h2>
        </div>
        <button
          onClick={() => handleSave()}
          disabled={saving}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px',
            background: saved ? '#059669' : '#2C3E2F', color: '#fff', border: 'none',
            borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            transition: 'background 0.2s',
          }}
        >
          <Save size={15} />
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
        Manage the frame styles and joinery options shown on the client-facing design questionnaire.
        Click any item to edit, drag images to replace, and reorder with arrows.
      </p>

      {renderStyleList('frameStyles', 'Frame Styles')}

      {/* Kit Selector for Portfolio */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>Kit Portfolio ({selectedKitIds.length} selected)</h3>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Section Title (shown to clients)</label>
          <input
            value={portfolioSectionTitle}
            onChange={e => setPortfolioSectionTitle(e.target.value)}
            onBlur={() => handleSave()}
            placeholder="Explore Full Timber Frame Designs"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 14, fontFamily: 'Inter, sans-serif' }}
          />
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12 }}>Select which kits from your shop to display in the questionnaire. Clients can like and comment on these.</p>
        {allKits.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No active kits found in shop.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            {allKits.map(kit => {
              const isSelected = selectedKitIds.includes(kit.id);
              const imgUrl = kit.thumbnailUrl || kit.thumbnail || (kit.galleryUrls?.[0]) || '';
              return (
                <div
                  key={kit.id}
                  onClick={() => {
                    const newIds = selectedKitIds.includes(kit.id)
                      ? selectedKitIds.filter(id => id !== kit.id)
                      : [...selectedKitIds, kit.id];
                    setSelectedKitIds(newIds);
                    handleSave(newIds);
                  }}
                  style={{
                    position: 'relative', borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                    border: isSelected ? '2px solid #2C3E2F' : '2px solid #E8E5E0',
                    background: 'var(--color-surface)', transition: 'border-color 0.2s',
                  }}
                >
                  <div style={{ height: 120, background: 'var(--pm-surface-hover)', overflow: 'hidden' }}>
                    {imgUrl ? (
                      <img src={imgUrl} alt={kit.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--color-text-muted)' }}>
                        <Image size={32} />
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '8px 10px' }}>
                    {isSelected ? (
                      <input
                        value={kitNameOverrides[kit.id] || kit.name}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setKitNameOverrides(prev => ({ ...prev, [kit.id]: e.target.value }))}
                        onBlur={() => handleSave()}
                        style={{ width: '100%', fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '3px 6px', fontFamily: 'Inter, sans-serif', background: 'var(--color-surface)' }}
                      />
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{kit.name}</div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{kit.type}{isSelected && kitNameOverrides[kit.id] && kitNameOverrides[kit.id] !== kit.name ? ` · was: ${kit.name}` : ''}</div>
                  </div>
                  {isSelected && (
                    <div style={{
                      position: 'absolute', top: 6, right: 6,
                      width: 22, height: 22, borderRadius: '50%',
                      background: '#2C3E2F', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Check size={14} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {renderStyleList('joineryStyles', 'Joinery Styles')}

      {/* Species Images */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>Wood Species Photos</h3>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 14 }}>
          Upload a photo of a completed frame for each wood species. These appear at the top of the species card in the questionnaire.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {SPECIES_LIST.map(sp => {
            const imgUrl = speciesImages[sp.id] || '';
            const isUploading = uploadingSpecies === sp.id;
            return (
              <div key={sp.id} style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ position: 'relative', height: 140, background: 'var(--pm-surface-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {imgUrl ? (
                    <img src={imgUrl} alt={sp.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Image size={32} style={{ color: 'var(--color-text-muted)' }} />
                  )}
                  {isUploading && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Loader2 size={20} className="spin" />
                    </div>
                  )}
                  <label style={{
                    position: 'absolute', inset: 0, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: imgUrl ? 'rgba(0,0,0,0.45)' : 'transparent',
                    opacity: imgUrl ? 0 : 1, transition: 'opacity 0.2s',
                    color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: 'Inter, sans-serif',
                    flexDirection: 'column', gap: 4,
                  }}
                  onMouseEnter={e => { if (imgUrl) e.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={e => { if (imgUrl) e.currentTarget.style.opacity = '0'; }}
                  >
                    <Image size={16} />
                    {imgUrl ? 'Change' : 'Upload Photo'}
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingSpecies(sp.id);
                        try {
                          const ext = file.name.split('.').pop() || 'jpg';
                          const storagePath = `media-library/questionnaire/species/${sp.id}.${ext}`;
                          const storageRef = ref(storage, storagePath);
                          await uploadBytes(storageRef, file);
                          const url = await getDownloadURL(storageRef);
                          setSpeciesImages(prev => ({ ...prev, [sp.id]: url }));
                          await setDoc(doc(db, 'appSettings', 'questionnaire'), {
                            speciesImages: { [sp.id]: url },
                            updatedAt: Timestamp.now(),
                          }, { merge: true });
                        } catch (err) { console.error('Error uploading species image:', err); }
                        finally { setUploadingSpecies(null); }
                      }}
                    />
                  </label>
                </div>
                <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{sp.name}</span>
                  {imgUrl && (
                    <button
                      onClick={async () => {
                        const updated = { ...speciesImages };
                        delete updated[sp.id];
                        setSpeciesImages(updated);
                        try {
                          await setDoc(doc(db, 'appSettings', 'questionnaire'), {
                            speciesImages: updated,
                            updatedAt: Timestamp.now(),
                          }, { merge: true });
                        } catch (err) { console.error('Error removing species image:', err); }
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error-text)', padding: 2 }}
                      title="Remove image"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireSettingsTab;
